/**
 * Search / Discover illustration — miniature wireframe of the discover page:
 * search bar at top, grid of course result cards below with gradient headers.
 */
import type { IllustrationProps } from './types';
import { usePalette } from './types';

export function SearchDiscover({ variant = 'on-gradient', className, style }: IllustrationProps) {
  const p = usePalette(variant);

  return (
    <svg
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {/* Card frame */}
      <rect
        x="4"
        y="4"
        width="112"
        height="112"
        rx="10"
        fill={p.frameFill}
        stroke={p.frameStroke}
        strokeWidth="1"
      />

      {/* Search bar */}
      <rect
        x="14"
        y="14"
        width="92"
        height="14"
        rx="7"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      {/* Magnifying glass icon */}
      <circle cx="24" cy="21" r="3.5" stroke={p.medium} strokeWidth="1.2" fill="none" />
      <line
        x1="26.5"
        y1="23.5"
        x2="29"
        y2="26"
        stroke={p.medium}
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      {/* Placeholder text */}
      <rect x="33" y="19.5" width="40" height="3" rx="1.5" fill={p.faint} />

      {/* Course card grid — 2×2 */}
      {/* Card 1: top-left */}
      <rect
        x="14"
        y="36"
        width="43"
        height="34"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="14" y="36" width="43" height="13" rx="5" fill={p.accent1} opacity="0.35" />
      <rect x="14" y="44" width="43" height="5" fill={p.accent1} opacity="0.35" />
      <rect x="19" y="55" width="30" height="3" rx="1.5" fill={p.subtle} />
      <rect x="19" y="61" width="20" height="2.5" rx="1.25" fill={p.faint} />

      {/* Card 2: top-right */}
      <rect
        x="63"
        y="36"
        width="43"
        height="34"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="63" y="36" width="43" height="13" rx="5" fill={p.accent3} opacity="0.35" />
      <rect x="63" y="44" width="43" height="5" fill={p.accent3} opacity="0.35" />
      <rect x="68" y="55" width="26" height="3" rx="1.5" fill={p.subtle} />
      <rect x="68" y="61" width="18" height="2.5" rx="1.25" fill={p.faint} />

      {/* Card 3: bottom-left */}
      <rect
        x="14"
        y="76"
        width="43"
        height="34"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="14" y="76" width="43" height="13" rx="5" fill={p.accent2} opacity="0.35" />
      <rect x="14" y="84" width="43" height="5" fill={p.accent2} opacity="0.35" />
      <rect x="19" y="96" width="34" height="3" rx="1.5" fill={p.subtle} />
      <rect x="19" y="102" width="22" height="2.5" rx="1.25" fill={p.faint} />

      {/* Card 4: bottom-right */}
      <rect
        x="63"
        y="76"
        width="43"
        height="34"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="63" y="76" width="43" height="13" rx="5" fill={p.accent1} opacity="0.25" />
      <rect x="63" y="84" width="43" height="5" fill={p.accent1} opacity="0.25" />
      <rect x="68" y="96" width="28" height="3" rx="1.5" fill={p.subtle} />
      <rect x="68" y="102" width="16" height="2.5" rx="1.25" fill={p.faint} />
    </svg>
  );
}
